// Error Boundary Component
import { Component, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { Button } from '@/components/ui'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ errorInfo })
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="max-w-md text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-[var(--ybh-red)]/10 border border-[var(--ybh-red)]/30 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-[var(--ybh-red)]" />
            </div>
            
            <h2 className="font-display text-2xl mb-2">Something went wrong</h2>
            <p className="text-[var(--text-secondary)] mb-6">
              An unexpected error occurred. This has been logged and we'll look into it.
            </p>

            {/* Error details (development only) */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-4 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] text-left">
                <p className="font-mono text-xs text-[var(--ybh-red)] mb-2">
                  {this.state.error.name}: {this.state.error.message}
                </p>
                {this.state.errorInfo && (
                  <pre className="font-mono text-[10px] text-[var(--text-muted)] overflow-auto max-h-32">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex items-center justify-center gap-3">
              <Button variant="secondary" onClick={this.handleRetry}>
                <RefreshCw className="w-4 h-4" />
                Try Again
              </Button>
              <Button variant="primary" onClick={this.handleGoHome}>
                <Home className="w-4 h-4" />
                Go Home
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Inline error display for non-critical errors
export function ErrorDisplay({ 
  title = 'Error loading content',
  message = 'Something went wrong. Please try again.',
  onRetry,
}: {
  title?: string
  message?: string
  onRetry?: () => void
}) {
  return (
    <div className="p-6 bg-[var(--bg-surface)] border border-[var(--ybh-red)]/30 text-center">
      <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-[var(--ybh-red)]" />
      <h3 className="font-medium text-[var(--text-primary)] mb-1">{title}</h3>
      <p className="text-sm text-[var(--text-secondary)] mb-4">{message}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          <RefreshCw className="w-4 h-4" />
          Retry
        </Button>
      )}
    </div>
  )
}

export default ErrorBoundary
